<script setup lang="ts">
import type { BTableSortBy } from 'bootstrap-vue-next';
import { BButton, BPagination } from 'bootstrap-vue-next';
import { computed, onMounted, ref } from 'vue';

import type { Mentorship } from '~/api/users';
import { fetchMentors } from '~/api/users';
import TableWithBusyState from '~/common/components/TableWithBusyState.vue';
import UserWithGitLabProfileLink from '~/common/components/UserWithGitLabProfileLink.vue';
import { dateFieldFormatter } from '~/common/FieldFormats';

import AddMentorshipModal from './AddMentorshipModal.vue';
import RemoveMentorshipModal from './RemoveMentorshipModal.vue';

const props = withDefaults(defineProps<{
  isAdmin?: boolean;
  isOwnProfile?: boolean;
  userHandle: string;
  showAddModal?: boolean;
}>(), {
  isAdmin: false,
  isOwnProfile: false,
  showAddModal: false,
});

const emit = defineEmits<{
  'count-updated': [count: number];
  'update:showAddModal': [isShown: boolean];
}>();

const mentors = ref<Mentorship[]>([]);
const loading = ref(false);
const showRemoveMentorshipModal = ref(false);
const mentorToRemove = ref<Mentorship | null>(null);
const currentPage = ref(1);
const perPage = ref(10);
const sortBy = ref<BTableSortBy[]>([{ key: 'username', order: 'asc' }]);

const showAddMentorshipModal = computed({
  get: () => props.showAddModal,
  set: isShown => emit('update:showAddModal', isShown),
});

const openRemoveModal = (mentor: Mentorship) => {
  mentorToRemove.value = mentor;
  showRemoveMentorshipModal.value = true;
};

const canEdit = computed(() => props.isAdmin || props.isOwnProfile);

const mentorFields = computed(() => [
  {
    key: 'username',
    label: 'Username',
    sortable: true,
  },
  {
    key: 'start_date',
    label: 'Start date',
    sortable: true,
    formatter: dateFieldFormatter,
  },
  ...(canEdit.value
    ? [{
        key: 'actions',
        label: '',
        sortable: false,
      }]
    : []),
]);

const loadMentors = async () => {
  loading.value = true;
  try {
    mentors.value = await fetchMentors(props.userHandle);
    emit('count-updated', mentors.value.length);
  } catch {
    mentors.value = [];
    emit('count-updated', 0);
  } finally {
    loading.value = false;
  }
};

const onMentorshipRemoved = () => {
  mentorToRemove.value = null;
  void loadMentors();
};

onMounted(loadMentors);
</script>

<template>
  <div>
    <TableWithBusyState
      v-model:sort-by="sortBy"
      :fields="mentorFields"
      :items="mentors"
      :busy="loading"
      :per-page="perPage"
      :current-page="currentPage"
      show-empty
    >
      <template #cell(username)="{ value }">
        <UserWithGitLabProfileLink :username="value" />
      </template>

      <template
        v-if="canEdit"
        #cell(actions)="{ item }"
      >
        <BButton
          type="button"
          class="float-end"
          variant="outline-danger"
          size="sm"
          :aria-label="`Remove ${item.username}`"
          @click="openRemoveModal(item)"
        >
          Remove
        </BButton>
      </template>
    </TableWithBusyState>

    <BPagination
      v-if="mentors.length > perPage"
      v-model="currentPage"
      :total-rows="mentors.length"
      :per-page="perPage"
      align="center"
      first-number
      last-number
      class="mt-3 mb-0"
      data-testid="mentors-pagination"
    />
  </div>

  <AddMentorshipModal
    v-model="showAddMentorshipModal"
    :user-handle="userHandle"
    @mentorship-added="loadMentors"
  />

  <RemoveMentorshipModal
    v-if="mentorToRemove"
    v-model="showRemoveMentorshipModal"
    :user-handle="userHandle"
    :mentor="mentorToRemove"
    @mentorship-removed="onMentorshipRemoved"
  />
</template>
